/**
 * Portfolio - Página de Apresentação da Plataforma G2eGente
 * Uma visão completa das funcionalidades e benefícios
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  Shield, 
  Users, 
  Briefcase, 
  MessageSquare, 
  CheckCircle2,
  ArrowRight,
  Building2,
  UserCheck,
  Lock,
  Smartphone,
  Brain,
  Star,
  Zap,
  BarChart3,
  FileText,
  Target,
  Award,
  TrendingUp,
  Clock,
  Heart,
  Globe,
  Layers,
  Settings,
  Play,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Instagram,
  ExternalLink
} from 'lucide-react';

export default function Portfolio() {
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState('recrutamento');

  // Módulos da plataforma
  const modules = {
    recrutamento: {
      title: 'Recrutamento Ético',
      icon: Briefcase,
      color: 'cyan',
      description: 'Processo seletivo baseado em consentimento e transparência',
      features: [
        { title: 'Consentimento LGPD', desc: 'Candidatos aprovam acesso aos dados', icon: Shield },
        { title: 'Trava por CNPJ', desc: 'Funcionários invisíveis para a própria empresa', icon: Lock },
        { title: 'Link Público', desc: 'Compartilhe vagas com QR Code', icon: Globe },
        { title: 'Ranking Inteligente', desc: 'Candidatos ordenados por compatibilidade', icon: TrendingUp },
      ]
    },
    engenharia: {
      title: 'Engenharia de Cargo',
      icon: Target,
      color: 'purple',
      description: 'Defina o perfil ideal antes de recrutar',
      features: [
        { title: 'Perfil DISC', desc: 'Análise comportamental completa', icon: Brain },
        { title: 'Competências', desc: 'Mapeamento de habilidades técnicas', icon: Star },
        { title: 'Níveis Hierárquicos', desc: 'Do estagiário ao diretor', icon: Layers },
        { title: 'Reutilização', desc: 'Use em múltiplas vagas', icon: FileText },
      ]
    },
    avaliacao: {
      title: 'Avaliação de Desempenho',
      icon: BarChart3,
      color: 'green',
      description: 'Avalie sua equipe de forma estruturada',
      features: [
        { title: 'Ciclos de Avaliação', desc: 'Períodos configuráveis', icon: Clock },
        { title: '4 Níveis de Análise', desc: 'Entrega, Comportamento, Realização, Valores', icon: Layers },
        { title: 'Feedback Estruturado', desc: 'Orientado ao desenvolvimento', icon: MessageSquare },
        { title: 'Relatórios PDF', desc: 'Exporte para apresentações', icon: FileText },
      ]
    },
    inclusao: {
      title: 'Inclusão & Diversidade',
      icon: Heart,
      color: 'pink',
      description: 'Promova equidade no seu processo seletivo',
      features: [
        { title: 'Selo Inclusivo', desc: 'Destaque para empresas comprometidas', icon: Award },
        { title: 'Perfis PCD', desc: 'Vagas afirmativas para PCDs', icon: UserCheck },
        { title: 'Profissionais 50+', desc: 'Inclusão de profissionais experientes', icon: Users },
        { title: 'Relatório ESG', desc: 'Métricas de diversidade', icon: BarChart3 },
      ]
    },
    eventual: {
      title: 'Trabalho Eventual',
      icon: Zap,
      color: 'amber',
      description: 'Encontre profissionais para demandas pontuais',
      features: [
        { title: '12 Categorias', desc: 'Garçom, limpeza, eventos e mais', icon: Layers },
        { title: 'Validação por IA', desc: 'Filtro de atividades ilícitas', icon: Brain },
        { title: 'Busca Rápida', desc: 'Encontre disponíveis agora', icon: Zap },
        { title: 'Histórico', desc: 'Avaliações de trabalhos anteriores', icon: Star },
      ]
    }
  };

  // Diferenciais
  const differentials = [
    {
      icon: Shield,
      title: 'LGPD Compliant',
      description: 'Plataforma desenvolvida em conformidade total com a Lei Geral de Proteção de Dados'
    },
    {
      icon: Brain,
      title: 'Inteligência Artificial',
      description: 'IA para validação de atividades, moderação de conteúdo e ranking de candidatos'
    },
    {
      icon: Smartphone,
      title: 'PWA Mobile',
      description: 'App instalável para candidatos com notificações push e acesso offline'
    },
    {
      icon: Globe,
      title: 'Multi-Empresa',
      description: 'Recrutadores podem gerenciar múltiplas empresas em uma única conta'
    },
    {
      icon: Lock,
      title: 'Dados Seguros',
      description: 'Criptografia, auditoria de acessos e controle granular de permissões'
    },
    {
      icon: TrendingUp,
      title: 'Relatórios Completos',
      description: 'Dashboards em tempo real com exportação em PDF para apresentações'
    }
  ];

  // Cases de uso
  const useCases = [
    {
      title: 'Empresas de Médio Porte',
      description: 'Gerencie todo o ciclo de recrutamento e avaliação em uma única plataforma',
      benefits: ['Redução de tempo de contratação', 'Compliance garantido', 'Relatórios para diretoria']
    },
    {
      title: 'Consultorias de RH',
      description: 'Atenda múltiplos clientes com a funcionalidade multi-empresa',
      benefits: ['Troca rápida de contexto', 'Vagas personalizadas por cliente', 'Banco de talentos centralizado']
    },
    {
      title: 'Eventos e Hotelaria',
      description: 'Encontre profissionais para demandas sazonais com trabalho eventual',
      benefits: ['Busca por disponibilidade imediata', 'Categorias específicas', 'Histórico de avaliações']
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200', light: 'bg-cyan-50' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200', light: 'bg-purple-50' },
      green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200', light: 'bg-green-50' },
      pink: { bg: 'bg-pink-100', text: 'text-pink-600', border: 'border-pink-200', light: 'bg-pink-50' },
      amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200', light: 'bg-amber-50' },
    };
    return colors[color] || colors.cyan;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">G2</span>
              </div>
              <div>
                <span className="font-bold text-xl text-slate-900">G2eGente</span>
                <span className="hidden sm:inline text-sm text-slate-500 ml-2">Recrutamento Ético</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/login')}>
                Entrar
              </Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={() => navigate('/register')}>
                Começar Grátis
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 via-white to-purple-50" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-cyan-100/50 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="mb-6 bg-cyan-100 text-cyan-700 hover:bg-cyan-100">
              <Zap className="w-3 h-3 mr-1" />
              Plataforma Completa de RH
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Recrutamento <span className="text-cyan-600">Ético</span> e{' '}
              <span className="text-purple-600">Inteligente</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
              Uma plataforma completa que conecta empresas aos melhores talentos com foco em 
              <strong> ética, consentimento e inclusão</strong>, em total conformidade com a LGPD.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                className="bg-cyan-600 hover:bg-cyan-700 text-lg px-8"
                onClick={() => navigate('/register')}
              >
                Começar Agora
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8"
                onClick={() => document.getElementById('modules')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Play className="w-5 h-5 mr-2" />
                Ver Funcionalidades
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-16 pt-8 border-t border-slate-200">
              <div className="text-center">
                <p className="text-3xl font-bold text-cyan-600">100%</p>
                <p className="text-sm text-slate-500">LGPD Compliant</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">5+</p>
                <p className="text-sm text-slate-500">Módulos Integrados</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">40</p>
                <p className="text-sm text-slate-500">Questões DISC</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-600">PWA</p>
                <p className="text-sm text-slate-500">App Mobile</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section id="modules" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-700">
              <Layers className="w-3 h-3 mr-1" />
              Módulos
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Uma Plataforma, Todas as Soluções
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Do recrutamento à avaliação de desempenho, tudo integrado em uma única plataforma
            </p>
          </div>

          <Tabs value={activeModule} onValueChange={setActiveModule} className="w-full">
            <TabsList className="grid grid-cols-2 sm:grid-cols-5 w-full mb-8 h-auto gap-2 bg-transparent">
              {Object.entries(modules).map(([key, module]) => {
                const Icon = module.icon;
                const colors = getColorClasses(module.color);
                return (
                  <TabsTrigger 
                    key={key} 
                    value={key}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all data-[state=active]:border-${module.color}-500 data-[state=active]:${colors.light}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-center">{module.title}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {Object.entries(modules).map(([key, module]) => {
              const Icon = module.icon;
              const colors = getColorClasses(module.color);
              return (
                <TabsContent key={key} value={key}>
                  <Card className={`border-2 ${colors.border}`}>
                    <CardHeader className={colors.light}>
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${colors.bg}`}>
                          <Icon className={`w-7 h-7 ${colors.text}`} />
                        </div>
                        <div>
                          <CardTitle className="text-2xl">{module.title}</CardTitle>
                          <CardDescription className="text-base">{module.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {module.features.map((feature, idx) => {
                          const FeatureIcon = feature.icon;
                          return (
                            <div key={idx} className="flex items-start gap-3 p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
                                <FeatureIcon className={`w-5 h-5 ${colors.text}`} />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{feature.title}</p>
                                <p className="text-sm text-slate-500">{feature.desc}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>

      {/* Differentials Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-100 text-green-700">
              <Star className="w-3 h-3 mr-1" />
              Diferenciais
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Por que Escolher o G2eGente?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Tecnologia de ponta aliada a princípios éticos e conformidade legal
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {differentials.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Card key={idx} className="border-slate-200 hover:border-cyan-300 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-cyan-600" />
                    </div>
                    <h3 className="font-semibold text-lg text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-100 text-amber-700">
              <Building2 className="w-3 h-3 mr-1" />
              Cases de Uso
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Ideal para Diferentes Cenários
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {useCases.map((useCase, idx) => (
              <Card key={idx} className="border-slate-200 hover:shadow-lg transition-all">
                <CardHeader>
                  <CardTitle className="text-xl">{useCase.title}</CardTitle>
                  <CardDescription>{useCase.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {useCase.benefits.map((benefit, bIdx) => (
                      <li key={bIdx} className="flex items-center gap-2 text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 to-cyan-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Pronto para Transformar seu RH?
          </h2>
          <p className="text-lg text-cyan-100 mb-8 max-w-2xl mx-auto">
            Comece hoje mesmo a recrutar de forma ética e inteligente. 
            Cadastre-se gratuitamente e descubra todo o potencial da plataforma.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-white text-cyan-600 hover:bg-cyan-50 text-lg px-8"
              onClick={() => navigate('/register')}
            >
              Criar Conta Grátis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-white text-white hover:bg-white/10 text-lg px-8"
              onClick={() => navigate('/login')}
            >
              Já tenho conta
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">G2</span>
                </div>
                <span className="font-bold text-xl text-white">G2eGente</span>
              </div>
              <p className="text-slate-400 mb-4 max-w-md">
                Plataforma de recrutamento ético que conecta empresas e talentos 
                com foco em consentimento, inclusão e conformidade com a LGPD.
              </p>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-cyan-600 transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-cyan-600 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Plataforma</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-cyan-400 transition-colors">Funcionalidades</a></li>
                <li><a href="#" className="hover:text-cyan-400 transition-colors">Preços</a></li>
                <li><a href="#" className="hover:text-cyan-400 transition-colors">Integrações</a></li>
                <li><a href="#" className="hover:text-cyan-400 transition-colors">Segurança</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-white mb-4">Contato</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-cyan-400" />
                  <span>contato@g2egente.com.br</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-cyan-400" />
                  <span>(11) 99999-9999</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-cyan-400" />
                  <span>São Paulo, SP</span>
                </li>
              </ul>
            </div>
          </div>

          <Separator className="my-8 bg-slate-700" />

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © 2026 G2eGente. Todos os direitos reservados.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="hover:text-cyan-400 transition-colors">Termos de Uso</a>
              <a href="#" className="hover:text-cyan-400 transition-colors">Política de Privacidade</a>
              <a href="#" className="hover:text-cyan-400 transition-colors">LGPD</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
